import AddIcon from '@mui/icons-material/Add'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import {
  DataGridPro,
  GridActionsCellItem,
  GridRenderCellParams,
  GridToolbar
} from '@mui/x-data-grid-pro'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import useGetInventoryPages from '../../../hooks/useGetInventoryPages/useGetInventoryPages'
import { LabelsInventoryParts } from '../../../models/Labels'
import { InventoryContext } from '../../../providers/InventoryProvider/InventoryProvider'
import { InventoryPartEntry } from '../../../sdk'
import { getPartAvailableQuantity } from '../../../utils/convertFluidsUnits/convertFluidsUnits'
import InventoryPaginationHeader from '../../InventoryPaginationHeader/InventoryPaginationHeader'
import CounterSaleInstanceStore from './CounterSaleInstanceStore'

const CounterSaleInventoryStore = () => {
  const [addInventoryProps, setAddInventoryProps] =
    useState<InventoryPartEntry>()
  const { view, setView } = useContext(InventoryContext)
  const {
    shopInventory,
    isLoading,
    autoGetNextPage,
    reachedEnd,
    getPage,
    getAllPages,
    cancelGetAllPages
  } = useGetInventoryPages({ stockedOnly: true, fluidsOnly: false })

  useEffect(() => {
    if (view !== 'stocked') {
      setView('stocked')
    }
  }, [setView, view])

  const closeDialog = useCallback(() => {
    setAddInventoryProps(undefined)
  }, [])

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: LabelsInventoryParts.name,
        flex: 2
      },
      {
        field: 'partNumber',
        headerName: LabelsInventoryParts.partNumber,
        flex: 2
      },
      {
        field: 'available',
        headerName: LabelsInventoryParts.available,
        flex: 1
      },
      {
        field: 'suggestedPricePerUnit',
        headerName: LabelsInventoryParts.suggestedPricePerUnit,
        flex: 1,
        sortable: false
      },
      {
        field: 'description',
        headerName: LabelsInventoryParts.description,
        flex: 2
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Add to Cart',
        flex: 2,
        getActions: (params: GridRenderCellParams) => {
          return [
            <GridActionsCellItem
              key="Add"
              icon={<AddIcon />}
              label="Add"
              onClick={() =>
                setAddInventoryProps(shopInventory?.allParts![params.id])
              }
            />
          ]
        }
      }
    ],
    [shopInventory?.allParts]
  )
  const rows = useMemo(() => {
    if (!shopInventory) {
      return []
    }
    return Object.keys(shopInventory.allParts!).map((id) => {
      const part = shopInventory.allParts![id]
      const available =
        getPartAvailableQuantity(id, shopInventory) +
        (part.info!.isFluids ? ' qt.' : '')
      return {
        id,
        ...part.info!,
        available
      }
    })
  }, [shopInventory])
  return (
    <>
      <Dialog
        open={!!addInventoryProps}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth>
        <DialogTitle>{addInventoryProps?.info?.name}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Part number: ${addInventoryProps?.info?.partNumber}`}
          </DialogContentText>
          {addInventoryProps && (
            <CounterSaleInstanceStore
              inventoryPart={addInventoryProps}
              closeDialog={closeDialog}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <InventoryPaginationHeader
        getPage={getPage}
        isLoading={isLoading}
        autoGetNextPage={autoGetNextPage}
        reachedEnd={reachedEnd}
        cancelGetAllPages={cancelGetAllPages}
        getAllPages={getAllPages}
        numPartsLoaded={Object.keys(shopInventory.allParts || {}).length}
        numPartsTotal={shopInventory.totalNumParts || 0}
      />
      <div style={{ height: 650, width: '100%' }}>
        <DataGridPro
          loading={isLoading || (autoGetNextPage && !reachedEnd)}
          slots={{
            toolbar: GridToolbar
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true
            }
          }}
          density="compact"
          columns={columns}
          rows={rows}
        />
      </div>
    </>
  )
}

export default CounterSaleInventoryStore
